import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Breadcrumbs,
    Link as MuiLink,
    Button,
    IconButton,
    Divider,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert,
    useTheme,
    alpha,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from "@mui/material";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";

/* Icons */
import FolderIcon from "@mui/icons-material/Folder";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PaletteIcon from "@mui/icons-material/Palette";

/* ---------- MEDIA API ---------- */
const MEDIA_BASE = "https://media.swancreate.com";
const API_FOLDERS = `${MEDIA_BASE}/folders`;
const API_UPLOAD = `${MEDIA_BASE}/upload`;
const API_MOVE = `${MEDIA_BASE}/operations/move`; // optional, graceful fallback

/* Tenant root under /DMS — change SITE_ROOT per site */
const SITE_ROOT = "ben";
const ROOT_LOCK = ["DMS"]; // never go above this
const START_PATH = [...ROOT_LOCK, SITE_ROOT]; // initial folder: DMS/Flagler

/* ---------- Helpers ---------- */
const pathJoin = (...parts) =>
    parts
        .filter(Boolean)
        .join("/")
        .replace(/\/+/g, "/")
        .replace(/^\/|\/$/g, "");

const isImage = (s = "") =>
    /^(image\/)/i.test(s) || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(s);

const isPdf = (s = "") => /application\/pdf/i.test(s) || /\.pdf$/i.test(s);

const guessType = (n = "") =>
    /\.png$/i.test(n)
        ? "image/png"
        : /\.(jpe?g)$/i.test(n)
            ? "image/jpeg"
            : /\.gif$/i.test(n)
                ? "image/gif"
                : /\.webp$/i.test(n)
                    ? "image/webp"
                    : /\.pdf$/i.test(n)
                        ? "application/pdf"
                        : "application/octet-stream";

const fileUrl = (folder, name) =>
    `${MEDIA_BASE}/images/${encodeURI(folder)}/${encodeURIComponent(name)}`;

const fmtBytes = (b) => {
    if (b == null) return "—";
    const u = ["B", "KB", "MB", "GB", "TB"];
    let v = b,
        i = 0;
    while (v >= 1024 && i < u.length - 1) {
        v /= 1024;
        i++;
    }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};

/* Folder color prefs (by absolute path) */
const getColors = () => {
    try {
        return JSON.parse(localStorage.getItem("dmsFolderColors") || "{}");
    } catch {
        return {};
    }
};
const setColors = (m) =>
    localStorage.setItem("dmsFolderColors", JSON.stringify(m || {}));

export default function DocumentManagerPage() {
    const theme = useTheme();

    /* -------- Navigation state -------- */
    const [segments, setSegments] = useState([...START_PATH]);
    const currentFolder = useMemo(() => pathJoin(...segments), [segments]);

    /* -------- Data state -------- */
    const [subfolders, setSubfolders] = useState([]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);

    /* -------- UI state -------- */
    const [snack, setSnack] = useState({
        open: false,
        severity: "success",
        msg: "",
    });
    const showSnack = useCallback(
        (severity, msg) => setSnack({ open: true, severity, msg }),
        []
    );
    const closeSnack = () =>
        setSnack((s) => ({
            ...s,
            open: false,
        }));

    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerFile, setViewerFile] = useState(null);

    /* dialogs */
    const [createOpen, setCreateOpen] = useState(false);
     const [newFolder, setNewFolder] = useState("");

    const [renameOpen, setRenameOpen] = useState(false); // placeholder
    const [renameValue, setRenameValue] = useState("");

    /* folder color controls */
    const [folderColors, setFolderColors] = useState(() => getColors());
    const [colorAnchorEl, setColorAnchorEl] = useState(null);
    const [colorForFolder, setColorForFolder] = useState(null);
    const colorInputRef = useRef(null);

    /* uploads */
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const dropRef = useRef(null);

    /* file actions menu */
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    /* -------- API calls -------- */

    // Ensure any folder path exists via POST /folders
    const ensureFolder = useCallback(async (folderPath) => {
        if (!folderPath || typeof folderPath !== "string") return;

        try {
            await axios.post(
                API_FOLDERS,
                { folder_name: folderPath },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );
            return;
        } catch (err) {
            const status = err?.response?.status;
            // 400/415/422 likely means wrong body for that mode; fall back to form
            if (status !== 400 && status !== 415 && status !== 422)
                throw err;
        }

        const form = new FormData();
        form.append("folder", folderPath);
        await axios.post(API_FOLDERS, form);
    }, []);

    const listSubfolders = useCallback(async (folderPath) => {
        const safe = encodeURI(folderPath); // keep slashes
        const { data } = await axios.get(`${API_FOLDERS}/${safe}/subfolders`);
        return data?.subfolders || [];
    }, []);

    const listImages = useCallback(async (folderPath) => {
        const safe = encodeURI(folderPath);
        const { data } = await axios.get(`${API_FOLDERS}/${safe}/images`);
        const rows = (data?.images || []).map((name) => ({
            id: `${folderPath}/${name}`,
            name,
            folder: folderPath,
            contentType: guessType(name),
            url: fileUrl(folderPath, name),
            sizeBytes: null,
            modified: null,
            type: "file",
        }));
        return rows;
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            // Make sure tenant roots exist
            await ensureFolder(pathJoin(...ROOT_LOCK)); // "DMS"
            await ensureFolder(pathJoin(...START_PATH)); // "DMS/Flagler"

            const [subs, imgs] = await Promise.all([
                listSubfolders(currentFolder).catch(() => []),
                listImages(currentFolder).catch(() => []),
            ]);
            setSubfolders(subs);
            setFiles(imgs);
        } catch (err) {
            console.error(err);
            showSnack("error", "Failed to load folder contents.");
        } finally {
            setLoading(false);
        }
    }, [currentFolder, ensureFolder, listSubfolders, listImages, showSnack]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    /* -------- Uploads -------- */
    const openFilePicker = () => fileInputRef.current?.click();

    const onPickFiles = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await doUpload(file, currentFolder);
        e.target.value = "";
    };

    const doUpload = async (file, destFolder) => {
        setUploading(true);
        try {
            await ensureFolder(destFolder);
            const form = new FormData();
            form.append("file", file, file.name);
            form.append("folder", destFolder);
            await axios.post(API_UPLOAD, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            showSnack("success", `Uploaded "${file.name}".`);
            await refresh();
        } catch (err) {
            console.error(err);
            showSnack("error", "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    // drag-from-desktop upload to right panel
    useEffect(() => {
        const zone = dropRef.current;
        if (!zone) return;
        const prevent = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };
        const onDrop = async (e) => {
            prevent(e);
            const f = e.dataTransfer?.files?.[0];
            if (f) await doUpload(f, currentFolder);
        };
        ["dragenter", "dragover", "dragleave", "drop"].forEach((ev) =>
            zone.addEventListener(ev, prevent)
        );
        zone.addEventListener("drop", onDrop);
        return () => {
            ["dragenter", "dragover", "dragleave", "drop"].forEach((ev) =>
                zone.removeEventListener(ev, prevent)
            );
            zone.removeEventListener("drop", onDrop);
        };
    }, [currentFolder]); // eslint-disable-line

    /* -------- File actions -------- */
    const deleteFile = async (row) => {
        try {
            const safeFolder = encodeURI(row.folder);
            const safeName = encodeURIComponent(row.name);
            await axios.delete(
                `${API_FOLDERS}/${safeFolder}/images/${safeName}`
            );
            showSnack("success", "Deleted.");
            await refresh();
        } catch (err) {
            console.error(err);
            showSnack("error", "Delete failed.");
        }
    };

    const moveFile = async ({ name, fromFolder, toFolder }) => {
        if (fromFolder === toFolder) return;
        try {
            await ensureFolder(toFolder);
            await axios.post(API_MOVE, {
                source: pathJoin(fromFolder, name),
                dest: pathJoin(toFolder, name),
            });
            showSnack("success", `Moved "${name}" to /${toFolder}`);
            await refresh();
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404 || status === 405) {
                showSnack(
                    "info",
                    "Move API not available yet. Ask devs to enable POST /operations/move."
                );
            } else {
                console.error(err);
                showSnack("error", "Move failed.");
            }
        }
    };

    /* -------- Folder explorer -------- */
    const goRoot = () => setSegments([...ROOT_LOCK]); // never above DMS
    const goUpOne = () => {
        if (segments.length <= ROOT_LOCK.length) return;
        setSegments((s) => s.slice(0, -1));
    };

    // Folder color
    const [colorMenuOpen, setColorMenuOpen] = useState(false);
    const handleOpenColor = (evt, absPath) => {
        setColorForFolder(absPath);
        setColorAnchorEl(evt.currentTarget);
        setColorMenuOpen(true);
        setTimeout(() => colorInputRef.current?.click(), 60);
    };
    const handleCloseColor = () => {
        setColorMenuOpen(false);
        setColorAnchorEl(null);
        setColorForFolder(null);
    };
    const onColorPicked = (hex) => {
        const map = { ...(folderColors || {}) };
        if (!hex) delete map[colorForFolder];
        else map[colorForFolder] = hex;
        setFolderColors(map);
        setColors(map);
    };

    const folderChipStyle = (absPath, i) => {
        const hex = folderColors?.[absPath];
        if (hex) {
            return {
                background: `linear-gradient(135deg, ${hex}33, ${hex})`,
                color: "#fff",
                border: `1px solid ${alpha(hex, 0.5)}`,
            };
        }
        const palette = [
            "#6366F1",
            "#22D3EE",
            "#10B981",
            "#3B82F6",
            "#EC4899",
            "#F59E0B",
        ];
        const c = palette[i % palette.length];
        return {
            background: `linear-gradient(135deg, ${alpha(
                c,
                0.25
            )}, ${alpha(c, 0.95)})`,
            color: "#fff",
            border: `1px solid ${alpha(c, 0.35)}`,
        };
    };

    /* -------- Drag in UI: file row → folder -------- */
    const dragDataKey = "dms-drag-file";
    const onRowDragStart = (row) => (ev) => {
        ev.dataTransfer.setData(
            dragDataKey,
            JSON.stringify({ name: row.name, folder: row.folder })
        );
        ev.dataTransfer.effectAllowed = "move";
    };
    const onFolderDragOver = (ev) => {
        if (ev.dataTransfer.types.includes(dragDataKey)) {
            ev.preventDefault();
            ev.dataTransfer.dropEffect = "move";
        }
    };
    const onFolderDrop = (targetAbsPath) => async (ev) => {
        ev.preventDefault();
        try {
            const payload = JSON.parse(ev.dataTransfer.getData(dragDataKey));
            await moveFile({
                name: payload.name,
                fromFolder: payload.folder,
                toFolder: targetAbsPath,
            });
        } catch {
            // ignore
        }
    };

    /* -------- Create Folder (at current) -------- */
    const createFolderAtCurrent = useCallback(
        async () => {
            const name = (newFolder || "").trim();
            if (!name) return;
            const target = pathJoin(currentFolder, name);
            try {
                // Just ensure the full path via /folders; backend will create .keep
                await ensureFolder(target);

                setCreateOpen(false);
                setNewFolder("");
                setSegments(target.split("/")); // jump into new folder
                showSnack("success", `Created /${target}`);
                // refresh will run via useEffect because currentFolder changes
            } catch (e) {
                console.error(e);
                showSnack("error", "Create failed.");
            }
        },
        [newFolder, currentFolder, ensureFolder, showSnack]
    );

    /* -------- Grid columns -------- */
    const columns = useMemo(
        () => [
            {
                field: "name",
                headerName: "Name",
                flex: 1.2,
                minWidth: 220,
                renderCell: (params) => {
                    const v = params.row;
                    const icon = isPdf(v.contentType || v.name) ? (
                        <PictureAsPdfIcon fontSize="small" />
                    ) : isImage(v.contentType || v.name) ? (
                        <ImageIcon fontSize="small" />
                    ) : (
                        <InsertDriveFileIcon fontSize="small" />
                    );
                    return (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{ minWidth: 0 }}
                        >
                            {icon}
                            <Typography
                                variant="body2"
                                noWrap
                                title={v.name}
                                sx={{ fontWeight: 600 }}
                            >
                                {v.name}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                field: "contentType",
                headerName: "Type",
                width: 140,
                valueFormatter: ({ value }) => value || "—",
            },
            {
                field: "sizeBytes",
                headerName: "Size",
                width: 120,
                valueFormatter: ({ value }) => fmtBytes(value),
            },
            {
                field: "modified",
                headerName: "Modified",
                width: 180,
                valueFormatter: ({ value }) =>
                    value ? new Date(value).toLocaleString() : "—",
            },
            {
                field: "actions",
                headerName: "",
                width: 160,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    const row = params.row;
                    return (
                        <Box display="flex" alignItems="center">
                            <Tooltip title="Preview">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setViewerFile(row);
                                            setViewerOpen(true);
                                        }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Download">
                                <IconButton
                                    size="small"
                                    component="a"
                                    href={row.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton
                                    size="small"
                                    onClick={() => deleteFile(row)}
                                >
                                    <DeleteForeverIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="More">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        setMenuAnchor(e.currentTarget);
                                        setMenuRow(row);
                                    }}
                                >
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                },
            },
        ],
        [] // eslint-disable-line
    );

    /* -------- Render -------- */
    return (
        <Box sx={{ p: { xs: 2, md: 3 }, position: "relative" }}>
            {/* Header */}
            <Box
                sx={{
                    borderRadius: 4,
                    p: { xs: 2.5, md: 3 },
                    mb: 3,
                    background: `linear-gradient(135deg, ${alpha(
                        theme.palette.primary.main,
                        0.3
                    )}, ${alpha(theme.palette.secondary.main, 0.35)})`,
                    backdropFilter: "blur(6px)",
                    border: `1px solid ${alpha(
                        theme.palette.divider,
                        0.2
                    )}`,
                    boxShadow: `0 10px 35px ${alpha(
                        theme.palette.primary.main,
                        0.35
                    )}`,
                }}
            >
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs>
                        <Typography
                            variant="h4"
                            sx={{
                                textShadow: `
                  0 0 16px ${alpha(
                                    theme.palette.primary.light,
                                    0.9
                                )},
                  0 0 40px ${alpha(
                                    theme.palette.secondary.light,
                                    0.6
                                )}`,
                                color: theme.palette.common.white,
                            }}
                            fontWeight={800}
                        >
                            Document Management
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                opacity: 0.9,
                                color: alpha(theme.palette.common.white, 0.9),
                            }}
                        >
                            Root locked to <strong>/DMS</strong> • Tenant root{" "}
                            <strong>/{pathJoin(...START_PATH)}</strong> •
                            Viewing: <strong>/{currentFolder}</strong>
                        </Typography>
                    </Grid>
                    <Grid item sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Create folder">
                            <Button
                                startIcon={<CreateNewFolderIcon />}
                                variant="contained"
                                color="secondary"
                                onClick={() => setCreateOpen(true)}
                            >
                                New Folder
                            </Button>
                        </Tooltip>
                        <Tooltip title={`Upload to /${currentFolder}`}>
                            <span>
                                <Button
                                    startIcon={<UploadFileIcon />}
                                    variant="contained"
                                    color="primary"
                                    disabled={uploading}
                                    onClick={openFilePicker}
                                >
                                    {uploading ? "Uploading…" : "Upload"}
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={onPickFiles}
                                    style={{ display: "none" }}
                                />
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>

                {/* Breadcrumbs */}
                <Box mt={2} display="flex" alignItems="center">
                    <Breadcrumbs aria-label="breadcrumb">
                        <MuiLink
                            component="button"
                            underline="hover"
                            color="inherit"
                            onClick={goRoot}
                            sx={{ color: theme.palette.grey[100] }}
                        >
                            Root
                        </MuiLink>
                        {segments.map((seg, idx) => (
                            <MuiLink
                                key={idx}
                                component="button"
                                underline="hover"
                                color={
                                    idx === segments.length - 1
                                        ? theme.palette.common.white
                                        : theme.palette.grey[200]
                                }
                                onClick={() =>
                                    setSegments(segments.slice(0, idx + 1))
                                }
                                sx={{
                                    fontWeight:
                                        idx === segments.length - 1 ? 800 : 500,
                                }}
                            >
                                {seg}
                            </MuiLink>
                        ))}
                    </Breadcrumbs>
                    <Box flexGrow={1} />
                    <Tooltip title="Up one (never above /DMS)">
                        <span>
                            <IconButton
                                onClick={goUpOne}
                                disabled={segments.length <= ROOT_LOCK.length}
                                sx={{
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.9
                                    ),
                                    color: theme.palette.common.white,
                                    "&:hover": {
                                        backgroundColor:
                                            theme.palette.primary.dark,
                                    },
                                }}
                            >
                                <ArrowBackIosNewIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Refresh">
                        <IconButton
                            onClick={refresh}
                            sx={{
                                ml: 1,
                                backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.9
                                ),
                                color: theme.palette.common.white,
                                "&:hover": {
                                    backgroundColor:
                                        theme.palette.primary.dark,
                                },
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Body: Left explorer + Right grid */}
            <Grid container spacing={2}>
                {/* LEFT: Folder Explorer */}
                <Grid item xs={12} md={4} lg={3}>
                    <Paper
                        variant="outlined"
                        sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            background: `linear-gradient(180deg, ${alpha(
                                theme.palette.background.paper,
                                0.9
                            )}, ${alpha(
                                theme.palette.background.default,
                                0.9
                            )})`,
                        }}
                    >
                        <Box
                            p={1.5}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="subtitle1"
                                fontWeight={800}
                                sx={{ color: theme.palette.grey[100] }}
                            >
                                Folders
                            </Typography>
                            <Chip
                                size="small"
                                label={`Subfolders: ${subfolders.length}`}
                                sx={{
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.85
                                    ),
                                    color: theme.palette.common.white,
                                }}
                            />
                        </Box>
                        <Divider />

                        <List dense disablePadding>
                            {/* Current folder (drop target) */}
                            <ListItemButton
                                selected
                                onClick={refresh}
                                onDragOver={onFolderDragOver}
                                onDrop={onFolderDrop(currentFolder)}
                            >
                                <ListItemIcon>
                                    <FolderIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{
                                        sx: { color: theme.palette.grey[100] },
                                    }}
                                    secondaryTypographyProps={{
                                        sx: {
                                            color: theme.palette.grey[400],
                                        },
                                    }}
                                    primary={`/${currentFolder}`}
                                    secondary="(current)"
                                />
                                <Tooltip title="Color">
                                    <IconButton
                                        size="small"
                                        onClick={(e) =>
                                            handleOpenColor(e, currentFolder)
                                        }
                                        sx={{
                                            backgroundColor: alpha(
                                                theme.palette.primary.main,
                                                0.85
                                            ),
                                            color: theme.palette.common.white,
                                            "&:hover": {
                                                backgroundColor:
                                                    theme.palette.primary.dark,
                                            },
                                        }}
                                    >
                                        <PaletteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </ListItemButton>

                            {/* Direct subfolders of currentFolder */}
                            {subfolders.map((name, i) => {
                                const abs = pathJoin(currentFolder, name);
                                const style = folderChipStyle(abs, i);
                                return (
                                    <ListItemButton
                                        key={abs}
                                        onClick={() =>
                                            setSegments(abs.split("/"))
                                        }
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            handleOpenColor(e, abs);
                                        }}
                                        sx={{ pl: 3 }}
                                        onDragOver={onFolderDragOver}
                                        onDrop={onFolderDrop(abs)}
                                    >
                                        <ListItemIcon>
                                            <Box
                                                sx={{
                                                    width: 28,
                                                    height: 22,
                                                    borderRadius: 1.2,
                                                    ...style,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <FolderIcon
                                                    sx={{ color: "#fff" }}
                                                    fontSize="small"
                                                />
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                sx: {
                                                    color:
                                                        theme.palette.grey[100],
                                                },
                                            }}
                                            secondaryTypographyProps={{
                                                sx: {
                                                    color:
                                                        theme.palette.grey[400],
                                                },
                                            }}
                                            primary={name}
                                            secondary={`/${abs}`}
                                        />
                                        <Tooltip title="Color">
                                            <IconButton
                                                size="small"
                                                onClick={(e) =>
                                                    handleOpenColor(e, abs)
                                                }
                                                sx={{
                                                    backgroundColor: alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        0.85
                                                    ),
                                                    color:
                                                        theme.palette.common
                                                            .white,
                                                    "&:hover": {
                                                        backgroundColor:
                                                            theme.palette
                                                                .primary.dark,
                                                    },
                                                }}
                                            >
                                                <PaletteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Paper>
                </Grid>

                {/* RIGHT: Files Grid with Pro features & Dropzone */}
                <Grid item xs={12} md={8} lg={9}>
                    <Paper
                        ref={dropRef}
                        variant="outlined"
                        sx={{
                            borderRadius: 3,
                            p: 1,
                            borderStyle: "dashed",
                            background: `linear-gradient(180deg, ${alpha(
                                theme.palette.background.paper,
                                0.95
                            )}, ${alpha(
                                theme.palette.background.default,
                                0.95
                            )})`,
                            "&:hover": {
                                borderColor: theme.palette.primary.main,
                            },
                        }}
                    >
                        <Box
                            px={1}
                            py={0.5}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="h6"
                                fontWeight={800}
                                sx={{ color: theme.palette.grey[100] }}
                            >
                                Files in /{currentFolder}
                            </Typography>
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${files.length} ${files.length === 1 ? "file" : "files"
                                    }`}
                                sx={{
                                    borderColor: alpha(
                                        theme.palette.primary.main,
                                        0.7
                                    ),
                                    color: theme.palette.grey[100],
                                }}
                            />
                        </Box>

                        <div style={{ height: 560, width: "100%" }}>
                            <DataGridPro
                                rows={files}
                                columns={columns}
                                getRowId={(r) => r.id}
                                disableRowSelectionOnClick
                                checkboxSelection
                                density="standard"
                                loading={loading}
                                slots={{ toolbar: GridToolbar }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 300 },
                                        csvOptions: {
                                            fileName:
                                                currentFolder.replace(
                                                    /\//g,
                                                    "_"
                                                ) + "_files",
                                        },
                                        printOptions: {
                                            disableToolbarButton: true,
                                        },
                                    },
                                }}
                                onCellMouseDown={(params, ev) => {
                                    if (params.field !== "name") return;
                                    const row = params.row;
                                    const target = ev.currentTarget;
                                    target.setAttribute("draggable", "true");
                                    const onDragStart = (e) =>
                                        onRowDragStart(row)(e);
                                    target.addEventListener(
                                        "dragstart",
                                        onDragStart,
                                        { once: true }
                                    );
                                    const cleanup = () =>
                                        target.removeAttribute("draggable");
                                    target.addEventListener(
                                        "dragend",
                                        cleanup,
                                        { once: true }
                                    );
                                }}
                                sx={{
                                    border: "none",
                                    color: theme.palette.grey[100],
                                    "& .MuiDataGrid-columnHeaders": {
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            0.45
                                        ),
                                        color: theme.palette.common.white,
                                        borderBottom: `1px solid ${alpha(
                                            theme.palette.common.white,
                                            0.2
                                        )}`,
                                    },
                                    "& .MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: 700,
                                        color: theme.palette.common.white,
                                    },
                                    "& .MuiDataGrid-cell": {
                                        color: theme.palette.grey[100],
                                    },
                                    "& .MuiDataGrid-row:hover": {
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            0.18
                                        ),
                                    },
                                    "& .MuiCheckbox-root svg": {
                                        fill: theme.palette.grey[100],
                                    },
                                    "& .MuiDataGrid-toolbarContainer": {
                                        borderBottom: `1px solid ${alpha(
                                            theme.palette.common.white,
                                            0.1
                                        )}`,
                                        backgroundColor: alpha(
                                            theme.palette.background.default,
                                            0.85
                                        ),
                                    },
                                    "& .MuiDataGrid-toolbarContainer .MuiButton-root":
                                    {
                                        backgroundColor:
                                            theme.palette.primary.main,
                                        color: theme.palette.common.white,
                                        textTransform: "none",
                                        borderRadius: 2,
                                        padding: "3px 10px",
                                        marginRight: 0.5,
                                        "&:hover": {
                                            backgroundColor:
                                                theme.palette.primary.dark,
                                        },
                                    },
                                    "& .MuiDataGrid-toolbarContainer .MuiInputBase-root":
                                    {
                                        backgroundColor: alpha(
                                            theme.palette.common.white,
                                            0.08
                                        ),
                                        borderRadius: 2,
                                        color: theme.palette.grey[100],
                                    },
                                    "& .MuiDataGrid-toolbarContainer .MuiSvgIcon-root":
                                    {
                                        color: theme.palette.grey[100],
                                    },
                                }}
                            />
                        </div>

                        <Box display="flex" justifyContent="center" py={1}>
                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                startIcon={<DriveFileMoveIcon />}
                                onClick={() =>
                                    showSnack(
                                        "info",
                                        "Tip: drag a row onto a folder in the left panel to move it."
                                    )
                                }
                            >
                                Tip: drag a file onto a folder to move
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Create Folder */}
            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Create Folder</DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={1}
                    >
                        Folder will be created under{" "}
                        <strong>/{currentFolder}</strong>. You can also create
                        nested like <code>2025/Reports</code>.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Folder name"
                        value={newFolder}
                        onChange={(e) => setNewFolder(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                createFolderAtCurrent();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCreateOpen(false)}
                        startIcon={<CloseIcon />}
                        variant="contained"
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={createFolderAtCurrent}
                        variant="contained"
                        color="primary"
                        startIcon={<CreateNewFolderIcon />}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rename placeholder (future: needs move API) */}
            <Dialog
                open={renameOpen}
                onClose={() => setRenameOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Rename (coming soon)</DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={1}
                    >
                        This requires a server-side move/copy+delete endpoint.
                    </Typography>
                    <TextField
                        fullWidth
                        label="New name"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setRenameOpen(false)}
                        startIcon={<CloseIcon />}
                        variant="contained"
                        color="primary"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Folder Color Picker */}
            <Menu
                anchorEl={colorAnchorEl}
                open={colorMenuOpen}
                onClose={handleCloseColor}
            >
                <MenuItem disableRipple>
                    <input
                        ref={colorInputRef}
                        type="color"
                        defaultValue={
                            folderColors?.[colorForFolder] || "#3B82F6"
                        }
                        onChange={(e) => onColorPicked(e.target.value)}
                        style={{
                            width: 48,
                            height: 32,
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                        }}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        color="inherit"
                        onClick={() => {
                            onColorPicked("");
                            handleCloseColor();
                        }}
                        sx={{ ml: 1 }}
                    >
                        Reset
                    </Button>
                </MenuItem>
            </Menu>

            {/* File actions menu (future extensibility) */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => {
                    setMenuAnchor(null);
                    setMenuRow(null);
                }}
            >
                <MenuItem disabled onClick={() => setRenameOpen(true)}>
                    Rename (requires move)
                </MenuItem>
            </Menu>

            {/* Viewer */}
            <Dialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, overflow: "hidden" },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <VisibilityIcon fontSize="small" /> {viewerFile?.name}
                    <Box flexGrow={1} />
                    <IconButton onClick={() => setViewerOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent
                    sx={{
                        p: 0,
                        background:
                            "radial-gradient(1200px 500px at 20% -10%, rgba(99,102,241,0.15), transparent), radial-gradient(800px 400px at 100% 120%, rgba(16,185,129,0.12), transparent)",
                    }}
                >
                    {viewerFile ? (
                        <Box
                            sx={{
                                width: "100%",
                                height: "70vh",
                                position: "relative",
                            }}
                        >
                            {isImage(
                                viewerFile.contentType || viewerFile.name
                            ) ? (
                                <Box
                                    component="img"
                                    alt={viewerFile.name}
                                    src={viewerFile.url}
                                    sx={{
                                        objectFit: "contain",
                                        width: "100%",
                                        height: "100%",
                                        display: "block",
                                        backgroundColor: alpha("#000", 0.05),
                                    }}
                                />
                            ) : isPdf(
                                viewerFile.contentType || viewerFile.name
                            ) ? (
                                <Box sx={{ width: "100%", height: "100%" }}>
                                    <embed
                                        src={viewerFile.url}
                                        type="application/pdf"
                                        width="100%"
                                        height="100%"
                                    />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexDirection: "column",
                                        gap: 2,
                                    }}
                                >
                                    <InsertDriveFileIcon fontSize="large" />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        align="center"
                                        px={3}
                                    >
                                        Preview not available for this file
                                        type. You can download it instead.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<DownloadIcon />}
                                        component="a"
                                        href={viewerFile.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Download
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box p={4}>
                            <Typography variant="body2">
                                No file selected.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snack.open}
                autoHideDuration={3600}
                onClose={closeSnack}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
            >
                <Alert
                    onClose={closeSnack}
                    severity={snack.severity}
                    variant="filled"
                    sx={{ boxShadow: 3 }}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
